"use client";

import { useId } from "react";
import { createPortal } from "react-dom";
import { useIsClient } from "usehooks-ts";
import CustomTooltip from "../CustomTooltip";
import PublicationStatusTooltipContent from "../PublicationStatusTooltipContent";
import Spinner from "../Spinner";
import Typography from "../Typography";
import type { PublicationStatusBadgeKind } from "../publicationFlow/publicationStatusBadge";
import { getPublicationStatusPresentation } from "../publicationFlow/publicationStatusPresentation";

export default function CalendarPostStatusBadge({
  kind,
  scheduledAt,
  errorMessage,
}: {
  kind: PublicationStatusBadgeKind;
  scheduledAt?: Date | null;
  errorMessage?: string | null;
}) {
  const tooltipId = useId();
  const isClient = useIsClient();
  const hasTooltip = kind !== "published" && kind !== "verifying";
  const label = kind === "scheduled"
    ? "Already scheduled"
    : getPublicationStatusPresentation(kind).label;
  const isInProgress = kind === "publishing" || kind === "verifying";
  return (
    <>
      <div
        role="status"
        tabIndex={hasTooltip ? 0 : undefined}
        data-tooltip-id={hasTooltip ? tooltipId : undefined}
        className={`${hasTooltip ? "pointer-events-auto" : "pointer-events-none"} flex w-fit max-w-full cursor-default items-center justify-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-slate-900 shadow-sm focus:outline-none`}
      >
        {isInProgress ? (
          <span aria-hidden>
            <Spinner className="size-3 motion-reduce:[&_svg]:animate-none" />
          </span>
        ) : null}
        <Typography variant="overline" className="truncate">{label}</Typography>
      </div>
      {/* Keep the tooltip outside the player's clipped container-query surface. */}
      {isClient && hasTooltip ? createPortal(
        <CustomTooltip
          id={tooltipId}
          openEvents={{ mouseover: true, focus: true, click: true }}
          closeEvents={{ mouseout: true, blur: true }}
          globalCloseEvents={{ escape: true, scroll: true, resize: true, clickOutsideAnchor: true }}
        >
          <PublicationStatusTooltipContent kind={kind} scheduledAt={scheduledAt} errorMessage={errorMessage} showLabel={false} />
        </CustomTooltip>,
        document.body,
      ) : null}
    </>
  );
}
